import { t, useStoreLanguage, getLocale, localizeDigits } from "../../../features/store-language/i18n.ts";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Input, Checkbox, Collapse, Badge, Button, Skeleton } from "antd";
import { MagnifyingGlass, CaretDown, X } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import "./SchemaFilter.css";
import { getStoreFilters } from "../storefrontService.ts";
import { normalizeStoreFilters, type StoreFilterOptions as FilterOptions, type StoreFilterGroup } from "../storefrontFilters.ts";

const { Panel } = Collapse;

const SCHEMA_FILTERS_STORAGE_KEY = "store_v1_schemaFilter_persistedFilters";

export interface SelectedFilter {
  key: keyof FilterOptions;
  id: number;
  label: string;
  category: string;
}

interface SchemaFilterProps {
  onFilterChange: (filters: FilterOptions) => void;
  onSelectedFiltersChange?: (selectedFilters: SelectedFilter[]) => void;
  clearFilterKey?: keyof FilterOptions | null;
  clearFilterId?: number | null;
  clearFilterVersion?: number;
  slug?: string;
  initialSearchTerm?: string;
  initialFilters?: FilterOptions;
  persistFilters?: boolean;
  resultsLoading?: boolean;
  mobileDrawer?: boolean;
  onMobileClose?: () => void;
}

const SchemaFilter: React.FC<SchemaFilterProps> = ({
  onFilterChange,
  onSelectedFiltersChange,
  clearFilterKey,
  clearFilterId,
  clearFilterVersion = 0,
  slug,
  initialSearchTerm,
  initialFilters,
  persistFilters = true,
  resultsLoading = false,
  mobileDrawer = false,
  onMobileClose,
}) => {
  const storeLanguage = useStoreLanguage();
  const [filterData, setFilterData] = useState<StoreFilterGroup[]>([]);
  const [filterDataLoading, setFilterDataLoading] = useState(true);
  const [filters, setFilters] = useState<FilterOptions>({});
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const panelsInitialized = useRef(false);
  const [panelSearchTerms, setPanelSearchTerms] = useState<{
    [key: string]: string;
  }>({});
  const isInitialMount = useRef(true);
  const hasInitializedRef = useRef(false);
  const previousSearchTermRef = useRef<string | undefined>(undefined);
  const previousSlugRef = useRef<string | undefined>(undefined);
  const filterRequestRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(function () {
    if (mobileDrawer) return undefined;
    const desktopViewport = window.matchMedia("(min-width: 1025px)");
    let animationFrame: number | undefined;

    function measureAvailableHeight() {
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(function () {
        const container = containerRef.current;
        if (!container) return;
        if (!desktopViewport.matches) {
          container.style.removeProperty("--schema-filter-viewport-height");
          return;
        }
        const top = Math.max(0, container.getBoundingClientRect().top);
        const availableHeight = Math.max(0, Math.floor(window.innerHeight - top - 12));
        const nextHeight = `${availableHeight}px`;
        if (container.style.getPropertyValue("--schema-filter-viewport-height") !== nextHeight) {
          container.style.setProperty("--schema-filter-viewport-height", nextHeight);
        }
      });
    }

    const resizeObserver = new ResizeObserver(measureAvailableHeight);
    const container = containerRef.current;
    const layout = container?.closest(".schema-layout");
    const breadcrumb = document.querySelector(".shop-breadcrumb--products");
    const header = document.querySelector(".shop-header");
    if (layout) resizeObserver.observe(layout);
    if (breadcrumb) resizeObserver.observe(breadcrumb);
    if (header) resizeObserver.observe(header);
    window.addEventListener("resize", measureAvailableHeight);
    window.addEventListener("scroll", measureAvailableHeight, { passive: true });
    desktopViewport.addEventListener("change", measureAvailableHeight);
    measureAvailableHeight();

    return function () {
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener("resize", measureAvailableHeight);
      window.removeEventListener("scroll", measureAvailableHeight);
      desktopViewport.removeEventListener("change", measureAvailableHeight);
    };
  }, [mobileDrawer]);

  function handlePanelChange(key: string | string[]) {
    setActiveKeys(Array.isArray(key) ? key : [key]);
  }

  useEffect(function () {
    if (panelsInitialized.current || !filterData.length) return;
    panelsInitialized.current = true;
    setActiveKeys(filterData.filter(function (group) {
      return group.code === "reeds" || group.code === "color";
    }).map(function (group) { return String(group.attributeDefinitionId); }));
  }, [filterData]);

  // Load persisted filters from localStorage (excluding searchTerm)
  const loadPersistedFilters = useCallback((): Partial<FilterOptions> => {
    if (initialFilters) return normalizeStoreFilters(initialFilters);
    if (!persistFilters) return {};
    try {
      const saved = localStorage.getItem(SCHEMA_FILTERS_STORAGE_KEY);
      if (saved) {
        const normalized = normalizeStoreFilters(JSON.parse(saved));
        return normalized.valueIds ? { valueIds: normalized.valueIds } : {};
      }
    } catch (error) {
      console.error("Error loading persisted filters:", error);
    }
    return {};
  }, [initialFilters, persistFilters]);

  // Save filters to localStorage (excluding searchTerm)
  const savePersistedFilters = useCallback((filters: FilterOptions) => {
    if (!persistFilters) return;
    try {
      const filtersToSave = { valueIds: filters.valueIds };

      localStorage.setItem(
        SCHEMA_FILTERS_STORAGE_KEY,
        JSON.stringify(filtersToSave),
      );
    } catch (error) {
      console.error("Error saving persisted filters:", error);
    }
  }, [persistFilters]);

  const handlePanelSearch = (panelKey: string, searchTerm: string) => {
    setPanelSearchTerms((prev) => ({
      ...prev,
      [panelKey]: searchTerm,
    }));
  };

  const fetchFilterData = useCallback(async (slugValue?: string) => {
    void storeLanguage; // Titles are reread in the current language.
    const requestId = ++filterRequestRef.current;
    try {
      setFilterDataLoading(true);

      const response = await getStoreFilters(slugValue);
      if (requestId === filterRequestRef.current) {
        setFilterData(response);
        const availableIds = new Set(response.flatMap(function (group) {
          return group.values.map(function (value) { return value.id; });
        }));
        setFilters(function (current) {
          const retained = current.valueIds?.filter(function (id) { return availableIds.has(id); });
          if (retained?.length === current.valueIds?.length) return current;
          return { ...current, valueIds: retained?.length ? retained : undefined };
        });
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    } finally {
      if (requestId === filterRequestRef.current) setFilterDataLoading(false);
    }
  }, [storeLanguage]);

  const previousLanguage = useRef(storeLanguage);
  useEffect(function () {
    if (previousLanguage.current !== storeLanguage) {
      previousLanguage.current = storeLanguage;
      void fetchFilterData(slug);
    }
  }, [storeLanguage, fetchFilterData, slug]);

  // Initial load with search term
  useEffect(() => {
    const searchTermChanged =
      previousSearchTermRef.current !== initialSearchTerm;
    if (initialSearchTerm && searchTermChanged) {
      previousSearchTermRef.current = initialSearchTerm;

      // Load persisted filters from localStorage
      const persistedFilters = loadPersistedFilters();

      // Set initial filters with search term and persisted filters
      const initialFilters: FilterOptions = {
        ...persistedFilters,
        searchTerm: initialSearchTerm,
      };
      setFilters(initialFilters);

      // Load filter data so users can further filter search results
      fetchFilterData(slug);

      // Reset isInitialMount so onFilterChange will be called when filters state updates
      isInitialMount.current = true;

      // Mark as initialized
      hasInitializedRef.current = true;

      // Call onFilterChange directly to trigger API call in SchemaList
      onFilterChange(initialFilters);
    }
  }, [storeLanguage,
    initialSearchTerm,
    slug,
    fetchFilterData,
    onFilterChange,
    loadPersistedFilters,
  ]);

  // Handle slug changes - fetch filter data when slug changes
  // This effect runs whenever slug changes (including undefined to value, value to value, or value to undefined)
  useEffect(() => {
    // Check if slug changed before updating the ref
    const slugChanged = previousSlugRef.current !== slug;

    // If slug changed, refresh filter data
    if (slugChanged) {
      // Update the ref to track current slug
      previousSlugRef.current = slug;

      // Reset flags when slug changes
      isInitialMount.current = true;
      hasInitializedRef.current = true;

      // Reset filter data to show loading state
      setFilterData([]);

      // Load persisted filters from localStorage
      const persistedFilters = loadPersistedFilters();

      // Set initial filters with persisted filters
      const initialFilters: FilterOptions = {
        ...persistedFilters,
      };
      if (initialSearchTerm) {
        initialFilters.searchTerm = initialSearchTerm;
      }
      setFilters(initialFilters);

      // Always call API with current slug to fetch fresh filter data
      fetchFilterData(slug);

      // Trigger onFilterChange for initial load if we have filters
      onFilterChange(initialFilters);
    }
  }, [storeLanguage,
    slug,
    initialSearchTerm,
    fetchFilterData,
    onFilterChange,
    loadPersistedFilters,
  ]);

  // Handle initial mount when there's no searchTerm and no slug
  // Load persisted filters and apply them (only once on initial mount)
  useEffect(() => {
    if (!hasInitializedRef.current && !initialSearchTerm && !slug) {
      hasInitializedRef.current = true;
      const persistedFilters = loadPersistedFilters();
      if (Object.keys(persistedFilters).length > 0) {
        setFilters(persistedFilters);
      }
      // Load filter data on initial mount
      fetchFilterData();
      onFilterChange(persistedFilters);
    }
  }, [storeLanguage, initialSearchTerm, slug, fetchFilterData, loadPersistedFilters, onFilterChange]);

  useEffect(() => {
    // Skip initial mount - don't trigger onFilterChange on first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Save filters to localStorage whenever they change (excluding searchTerm)
    savePersistedFilters(filters);

    // Trigger onFilterChange for all filter changes
    onFilterChange(filters);
  }, [storeLanguage, filters, onFilterChange, savePersistedFilters]);

  function handleCheckboxChange(group: StoreFilterGroup, checkedValues: number[]) {
    const groupIds = new Set(group.values.map(function (option) { return option.id; }));
    setFilters(function (previous) {
      const remaining = (previous.valueIds || []).filter(function (id) { return !groupIds.has(id); });
      const valueIds = [...remaining, ...checkedValues];
      return { ...previous, valueIds: valueIds.length ? valueIds : undefined };
    });
  }

  // Process every badge-removal request in each mounted filter instance.
  useEffect(
    function () {
      if (!clearFilterKey || clearFilterVersion === 0) {
        return;
      }

      setFilters(function (previousFilters) {
        const newFilters = { ...previousFilters };
        const currentValue = previousFilters[clearFilterKey];

        if (
          clearFilterKey !== "searchTerm" &&
          clearFilterId !== null &&
          clearFilterId !== undefined &&
          Array.isArray(currentValue)
        ) {
          const newArray = currentValue.filter(function (id) {
            return id !== clearFilterId;
          });
          newFilters[clearFilterKey] =
            newArray.length > 0 ? newArray : undefined;
        } else {
          delete newFilters[clearFilterKey];
        }

        return newFilters;
      });
    },
    [clearFilterId, clearFilterKey, clearFilterVersion],
  );

  // Notify parent about selected filters with labels
  useEffect(() => {
    if (!onSelectedFiltersChange) return;

    const selectedFilters: SelectedFilter[] = [];

    // Add searchTerm to selected filters if present
    if (filters.searchTerm) {
      selectedFilters.push({
        key: "searchTerm",
        id: 0,
        label: filters.searchTerm,
        category: t("جستجو"),
      });
    }

    filterData.forEach(function (group) {
      group.values.forEach(function (value) {
        if (filters.valueIds?.includes(value.id)) {
          selectedFilters.push({
            key: "valueIds",
            id: value.id,
            label: value.title,
            category: group.title,
          });
        }
      });
    });

    onSelectedFiltersChange(selectedFilters);
  }, [storeLanguage, filters, filterData, onSelectedFiltersChange]);

  function clearAllFilters() {
    setFilters({});
  }

  const selectedFilterCount = Object.entries(filters).reduce(function (
    total,
    entry,
  ) {
    const value = entry[1];
    if (Array.isArray(value)) {
      return total + value.length;
    }
    return value ? total + 1 : total;
  }, 0);

  function renderFilterPanel(group: StoreFilterGroup) {
    const key = String(group.attributeDefinitionId);
    const title = group.title;
    const options = group.values;
    const selectedValues = (filters.valueIds || []).filter(function (id) {
      return options.some(function (option) { return option.id === id; });
    });
    const hasSelection = selectedValues.length > 0;
    const searchTerm = panelSearchTerms[key] || "";
    // Filter options based on search term
    const filteredOptions = activeKeys.includes(key) ? options.filter((option) => {
      const optionTitle = option.title;
      return optionTitle.toLowerCase().includes(searchTerm.toLowerCase());
    }) : [];

    return (
      <Panel
        header={
          <div className="filter-panel-header">
            <div className="filter-panel-header-row">
              <span className="filter-panel-heading">
                <span className="filter-panel-title">{title}</span>
              </span>
              {hasSelection && (
                <Badge count={selectedValues.length > 99 ? localizeDigits("99+") : selectedValues.length.toLocaleString(getLocale())} />
              )}
            </div>
          </div>
        }
        key={key}
        className="filter-panel"
      >
        {activeKeys.includes(key) && options.length > 5 && (
          <Input
            placeholder={t("جستجو در {{value0}}...", { value0: title })}
            aria-label={t("جستجو در {{value0}}...", { value0: title })}
            value={searchTerm}
            onChange={function (event) { handlePanelSearch(key, event.target.value); }}
            allowClear
            prefix={<MagnifyingGlass className="panel-search-icon" />}
            className="filter-panel-search"
          />
        )}
        {activeKeys.includes(key) && (
        <Checkbox.Group
          value={selectedValues}
          onChange={function (values) {
            handleCheckboxChange(group, values as number[]);
          }}
          className={`filter-checkbox-group${options.some(function (option) { return Boolean(option.hexCode); }) ? " filter-checkbox-group--colors" : ""}`}
        >
          {filteredOptions.map((option) => (
            <Checkbox
              key={option.id}
              value={option.id}
              disabled={resultsLoading || (!selectedValues.includes(option.id) && (filters.valueIds?.length || 0) >= 100)}
              className="filter-checkbox"
            >
              {option.hexCode && (
                <span
                  className="filter-color-swatch"
                  style={{ backgroundColor: option.hexCode }}
                  aria-hidden="true"
                />
              )}
              <span className="filter-option-label">
                {option.title}
              </span>
            </Checkbox>
          ))}
          {filteredOptions.length === 0 && (
            <div className="filter-no-results">{t("موردی یافت نشد")}</div>
          )}
        </Checkbox.Group>
        )}
      </Panel>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className={`schema-filter-container schema-filter-gallery${mobileDrawer ? " schema-filter-container--mobile" : ""}`}
    >
      <div className="schema-filter-card">
        <div className="schema-filter-header">
          <div>
            <span className="filter-title">{t("فیلتر ها")}</span>
          </div>
          {!mobileDrawer &&
            selectedFilterCount > 0 &&
            !filterDataLoading && (
              <Button
                type="text"
                className="filter-clear-btn"
                onClick={clearAllFilters}
                disabled={resultsLoading}
              >{t("پاک کردن همه")} <span className="filter-clear-count">
                  {selectedFilterCount.toLocaleString(getLocale())}
                </span>
              </Button>
            )}
        </div>

        {selectedFilterCount > 0 && !filterDataLoading && (
          <div className="filter-selected-chips">
            {filterData.flatMap(function (group) {
              return group.values.filter(function (value) {
                return filters.valueIds?.includes(value.id);
              }).map(function (value) {
                return (
                  <button
                    key={value.id}
                    type="button"
                    disabled={resultsLoading}
                    onClick={function () {
                      setFilters(function (previous) {
                        const remaining = previous.valueIds?.filter(function (id) { return id !== value.id; });
                        return { ...previous, valueIds: remaining?.length ? remaining : undefined };
                      });
                    }}
                    aria-label={t("حذف {{value0}}", { value0: `${group.title}: ${value.title}` })}
                  >
                    <span>{value.title}</span>
                    <X size={14} aria-hidden="true" />
                  </button>
                );
              });
            })}
          </div>
        )}

        {filterDataLoading ? (
          <div
            className="schema-filter-loading"
            aria-label={t("در حال بارگذاری فیلترها")}
            aria-busy="true"
            role="status"
          >
            {[0, 1, 2, 3].map(function (item) {
              return (
                <div className="filter-skeleton-row" key={item}>
                  <span className="filter-skeleton-copy">
                    <Skeleton.Input
                      active
                      size="small"
                      className="filter-skeleton-title"
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      className="filter-skeleton-summary"
                    />
                  </span>
                  <Skeleton.Input
                    active
                    size="small"
                    className="filter-skeleton-chevron"
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <Collapse
            ghost
            expandIconPosition="end"
            activeKey={activeKeys}
            expandIcon={function ({ isActive }) {
              return (
                <CaretDown
                  size={16}
                  aria-hidden="true"
                  style={{ transform: isActive ? "rotate(180deg)" : undefined }}
                />
              );
            }}
            onChange={handlePanelChange}
            className="filter-collapse"
          >
            {[...filterData].sort(function (left, right) {
              function priority(group: StoreFilterGroup) {
                return group.code === "reeds" ? 0 : group.code === "color" ? 1 : 2;
              }
              return priority(left) - priority(right);
            }).map(renderFilterPanel)}
          </Collapse>
        )}

        {mobileDrawer && (
          <div
            className={`schema-filter-mobile-actions${
              selectedFilterCount > 0 && !filterDataLoading
                ? " schema-filter-mobile-actions--has-filters"
                : ""
            }`}
          >
            {selectedFilterCount > 0 && !filterDataLoading && (
              <Button
                type="default"
                className="schema-filter-mobile-clear"
                onClick={clearAllFilters}
              >{t("پاک کردن همه")} </Button>
            )}
            <Button
              type="primary"
              className="schema-filter-mobile-submit"
              onClick={onMobileClose}
              loading={resultsLoading}
            >{t("نمایش نتایج")} {selectedFilterCount > 0 ? ` (${localizeDigits(selectedFilterCount)})` : ""}
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SchemaFilter;
